import { Component } from '@theme/component';
import { CartUpdateEvent } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

if (!customElements.get('cart-recommendations')) {
  class CartRecommendations extends Component {
    constructor() {
      super();

      this.STORAGE_KEY = 'cart_recommendations_state';
      this.isRestoringState = false;

      this.onRadioChange = this.onRadioChange.bind(this);
      this.onMinusClick = this.onMinusClick.bind(this);
      this.onPlusClick = this.onPlusClick.bind(this);
      this.onInputChange = this.onInputChange.bind(this);
      this.clearCheckedRadiosImmediately =
        this.clearCheckedRadiosImmediately.bind(this);
    }

    connectedCallback() {
      super.connectedCallback();

      requestAnimationFrame(() => {
        this.initialize();
      });

      this.handleCartUpdate = () => {
        this.clearCheckedRadiosImmediately();

        setTimeout(() => {
          this.unbindEvents();
          this.initialize();
          setTimeout(() => {
            this.checkIfRecommendationWasRemoved();
          }, 300);
        }, 50);
      };

      document.addEventListener('cart:update', this.handleCartUpdate);
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.unbindEvents();

      if (this.handleCartUpdate) {
        document.removeEventListener('cart:update', this.handleCartUpdate);
      }
    }

    updatedCallback() {
      this.clearCheckedRadiosImmediately();

      this.unbindEvents();

      setTimeout(() => {
        this.initialize();
        setTimeout(() => {
          this.checkIfRecommendationWasRemoved();
        }, 100);
      }, 200);
    }

    initialize() {
      this.form = this.querySelector('#cart-recommendations-form');

      if (!this.form) {
        requestAnimationFrame(() => this.initialize());
        return;
      }

      this.radioInputs = this.form.querySelectorAll(
        '.cart-drawer-recommendations__radio-input'
      );

      this.items = this.form.querySelectorAll(
        '.cart-drawer-recommendations__item'
      );

      if (
        !this.radioInputs ||
        this.radioInputs.length === 0 ||
        !this.items ||
        this.items.length === 0
      ) {
        requestAnimationFrame(() => this.initialize());
        return;
      }
      this.init();
      this.bindEvents();
      this.restoreState();
    }

    init() {
      if (!this.items) return;
      this.items.forEach((item) => {
        this.disableQuantity(item);
      });
    }

    clearCheckedRadiosImmediately() {
      const form =
        this.form || this.querySelector('#cart-recommendations-form');
      if (!form) return;

      const checkedRadios = form.querySelectorAll(
        '.cart-drawer-recommendations__radio-input:checked'
      );

      if (!checkedRadios || checkedRadios.length === 0) return;

      checkedRadios.forEach((radioElement) => {
        const radio = /** @type {HTMLInputElement} */ (radioElement);
        radio.checked = false;

        const item = radio.closest('.cart-drawer-recommendations__item');
        if (item) {
          const { input, minusBtn } = this.getQuantityElements(item);
          if (input) input.value = 1;
          if (minusBtn) minusBtn.disabled = true;
          this.disableQuantity(item);
        }
      });

      this.resetCartTotal();
    }

    saveState() {
      const selectedRadio = this.form?.querySelector(
        '.cart-drawer-recommendations__radio-input:checked'
      );

      if (!selectedRadio) {
        sessionStorage.removeItem(this.STORAGE_KEY);
        return;
      }

      const selectedItem = selectedRadio.closest(
        '.cart-drawer-recommendations__item'
      );
      const productId = /** @type {HTMLElement} */ (selectedItem)?.dataset
        ?.productId;
      const { input } = this.getQuantityElements(selectedItem);
      const quantity = input?.value || 1;

      const stateData = {
        productId,
        quantity: parseInt(quantity, 10),
        timestamp: Date.now(),
      };

      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(stateData));
    }

    async restoreState() {
      const savedState = sessionStorage.getItem(this.STORAGE_KEY);

      if (!savedState) {
        return;
      }

      try {
        const { productId, quantity, timestamp } = JSON.parse(savedState);

        if (Date.now() - timestamp > 30000) {
          sessionStorage.removeItem(this.STORAGE_KEY);
          return;
        }

        const productItem = Array.from(this.items || []).find(
          (item) =>
            /** @type {HTMLElement} */ (item).dataset.productId === productId
        );

        if (!productItem) {
          return;
        }

        const radio = productItem.querySelector(
          '.cart-drawer-recommendations__radio-input'
        );

        if (radio) {
          const variantId = /** @type {HTMLInputElement} */ (radio).value;
          const isInCart = await this.isVariantInCart(variantId);

          if (!isInCart) {
            sessionStorage.removeItem(this.STORAGE_KEY);
            return;
          }

          this.isRestoringState = true;
          this.unbindEvents();

          if (!(/** @type {HTMLInputElement} */ (radio).checked)) {
            /** @type {HTMLInputElement} */ (radio).click();
          }

          const { input } = this.getQuantityElements(productItem);
          if (input) {
            input.value = quantity;
            this.updateMinusState(input);
          }

          this.updateCartTotal();
          this.bindEvents();
          this.isRestoringState = false;
        }
      } catch (error) {
        console.error('Error restoring recommendations state:', error);
        sessionStorage.removeItem(this.STORAGE_KEY);
      }
    }

    async isVariantInCart(variantId) {
      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        const cartVariantIds = /** @type {any[]} */ (cart.items).map(
          (item) => item.variant_id
        );
        return cartVariantIds.includes(parseInt(variantId));
      } catch (error) {
        console.error(
          '[Recommendation] Error checking if variant in cart:',
          error
        );
        return false;
      }
    }

    bindEvents() {
      if (!this.radioInputs || !this.items) {
        return;
      }

      this.radioInputs.forEach((radio) => {
        radio.addEventListener('change', this.onRadioChange);
      });

      this.items.forEach((item) => {
        const { minusBtn, plusBtn, input } = this.getQuantityElements(item);

        if (minusBtn) {
          minusBtn.addEventListener('click', this.onMinusClick);
        }
        if (plusBtn) {
          plusBtn.addEventListener('click', this.onPlusClick);
        }
        if (input) {
          input.addEventListener('change', this.onInputChange);
        }
      });
    }

    unbindEvents() {
      if (!this.radioInputs || !this.items) return;

      this.radioInputs.forEach((radio) => {
        radio.removeEventListener('change', this.onRadioChange);
      });

      this.items.forEach((item) => {
        const { minusBtn, plusBtn, input } = this.getQuantityElements(item);

        minusBtn?.removeEventListener('click', this.onMinusClick);
        plusBtn?.removeEventListener('click', this.onPlusClick);
        input?.removeEventListener('change', this.onInputChange);
      });
    }

    async checkIfRecommendationWasRemoved() {
      const checkedRadios = this.form?.querySelectorAll(
        '.cart-drawer-recommendations__radio-input:checked'
      );

      if (!checkedRadios || checkedRadios.length === 0) {
        return;
      }

      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        const cartVariantIds = cart.items.map(
          (item) => /** @type {any} */ (item).variant_id
        );

        checkedRadios.forEach((radioElement) => {
          const radio = /** @type {HTMLInputElement} */ (radioElement);
          const variantId = radio.value;
          const isInCart = cartVariantIds.includes(parseInt(variantId));

          if (!isInCart) {
            radio.checked = false;

            radio.dispatchEvent(new Event('change', { bubbles: true }));

            const item = radio.closest('.cart-drawer-recommendations__item');
            if (item) {
              this.resetQuantity(item);
              this.disableQuantity(item);
            }

            this.resetCartTotal();

            sessionStorage.removeItem(this.STORAGE_KEY);
          }
        });
      } catch (error) {
        console.error(
          '[Recommendation] Error checking if product was removed:',
          error
        );
      }
    }

    async onRadioChange(event) {
      if (this.isRestoringState) {
        return;
      }

      const radio = event.currentTarget;

      if (!radio.checked) {
        this.resetCartTotal();
        return;
      }

      if (!this.items) {
        return;
      }

      // Get previous selection before updating
      const previousRadio = this.form?.querySelector(
        '.cart-drawer-recommendations__radio-input:checked:not([data-just-checked])'
      );
      radio.setAttribute('data-just-checked', 'true');

      const previousItem = previousRadio?.closest(
        '.cart-drawer-recommendations__item'
      );
      const previousVariantId = previousRadio
        ? /** @type {HTMLInputElement} */ (previousRadio).value
        : undefined;

      const selectedItem = radio.closest('.cart-drawer-recommendations__item');

      const variantId = radio.value;
      const { input } = this.getQuantityElements(selectedItem);
      const quantity = parseInt(input?.value, 10) || 1;

      this.items.forEach((item) => {
        if (item !== selectedItem) {
          this.resetQuantity(item);
          this.disableQuantity(item);
        }
      });

      if (selectedItem) {
        this.enableQuantity(selectedItem);

        if (previousVariantId && previousVariantId !== variantId) {
          await this.removeFromCart(previousVariantId);
        }

        await this.addToCart(variantId, quantity);

        this.updateCartTotal();
        this.saveState();
      }

      radio.removeAttribute('data-just-checked');
    }

    async onMinusClick(event) {
      const item = event.currentTarget.closest(
        '.cart-drawer-recommendations__item'
      );

      if (!item) {
        return;
      }

      const { input } = this.getQuantityElements(item);
      const oldQuantity = parseInt(input?.value, 10) || 1;

      this.updateQuantity(input, -1);

      const newQuantity = parseInt(input?.value, 10) || 1;

      if (oldQuantity !== newQuantity) {
        const radio = item.querySelector(
          '.cart-drawer-recommendations__radio-input:checked'
        );
        if (radio) {
          const variantId = radio.value;
          await this.updateCartQuantity(variantId, newQuantity);
        }
      }

      this.updateCartTotal();
      this.saveState();
    }

    async onPlusClick(event) {
      const item = event.currentTarget.closest(
        '.cart-drawer-recommendations__item'
      );

      if (!item) {
        return;
      }

      const { input } = this.getQuantityElements(item);
      const oldQuantity = parseInt(input?.value, 10) || 1;

      this.updateQuantity(input, 1);

      const newQuantity = parseInt(input?.value, 10) || 1;

      if (oldQuantity !== newQuantity) {
        const radio = item.querySelector(
          '.cart-drawer-recommendations__radio-input:checked'
        );
        if (radio) {
          const variantId = radio.value;
          await this.updateCartQuantity(variantId, newQuantity);
        }
      }

      this.updateCartTotal();
      this.saveState();
    }

    onInputChange(event) {
      const input = event.currentTarget;
      this.normalizeQuantity(input);
      this.updateCartTotal();
      this.saveState();
    }

    getQuantityElements(item) {
      // Find all wrappers and select the visible one
      const wrappers = item.querySelectorAll(
        '.cart-drawer-recommendations__quantity-wrapper'
      );
      
      let wrapper = null;
      
      // If there are multiple wrappers, find the visible one
      if (wrappers.length > 1) {
        for (const w of wrappers) {
          const style = window.getComputedStyle(w);
          if (style.display !== 'none') {
            wrapper = w;
            break;
          }
        }
      } else if (wrappers.length === 1) {
        // Only one wrapper, use it
        wrapper = wrappers[0];
      }

      return {
        wrapper,
        input: wrapper?.querySelector('.quantity-input'),
        minusBtn: wrapper?.querySelector('[data-action="decrease"]'),
        plusBtn: wrapper?.querySelector('[data-action="increase"]'),
      };
    }

    updateQuantity(input, delta) {
      if (!input) return;

      const min = parseInt(input.min, 10) || 1;
      const max = parseInt(input.max, 10) || 99;
      const current = parseInt(input.value, 10) || min;

      const next = Math.max(min, Math.min(max, current + delta));
      input.value = next;

      this.updateMinusState(input);
      
      // Update the total price for this item
      const item = input.closest('.cart-drawer-recommendations__item');
      if (item) {
        this.updateItemTotalPrice(item, next);
      }
    }

    updateItemTotalPrice(item, quantity) {
      const totalPriceElement = item.querySelector('.cart-drawer-recommendations__total-price');
      if (!totalPriceElement) return;

      const basePrice = parseFloat(totalPriceElement.getAttribute('data-base-price')) || 0;
      const totalPrice = basePrice * quantity;

      totalPriceElement.textContent = this.formatMoney(totalPrice);
    }

    normalizeQuantity(input) {
      if (!input) return;

      const min = parseInt(input.min, 10) || 1;
      const max = parseInt(input.max, 10) || 99;

      let value = parseInt(input.value, 10) || min;
      value = Math.max(min, Math.min(max, value));

      input.value = value;
      this.updateMinusState(input);
      
      // Update the total price for this item
      const item = input.closest('.cart-drawer-recommendations__item');
      if (item) {
        this.updateItemTotalPrice(item, value);
      }
    }

    updateMinusState(input) {
      const item = input.closest('.cart-drawer-recommendations__item');

      const { minusBtn } = this.getQuantityElements(item);
      const min = parseInt(input.min, 10) || 1;

      if (minusBtn) {
        minusBtn.disabled = parseInt(input.value, 10) <= min;
      }
    }

    resetQuantity(item) {
      const { input, minusBtn } = this.getQuantityElements(item);

      if (input) {
        input.value = 1;
        // Update the total price when resetting
        this.updateItemTotalPrice(item, 1);
      }
      if (minusBtn) minusBtn.disabled = true;
    }

    disableQuantity(item) {
      const { wrapper } = this.getQuantityElements(item);
      if (!wrapper) return;

      wrapper.classList.remove('active');
    }

    enableQuantity(item) {
      const { wrapper } = this.getQuantityElements(item);
      if (!wrapper) return;

      wrapper.classList.add('active');
    }

    updateCartTotal() {
      const selectedRadio = this.form?.querySelector(
        '.cart-drawer-recommendations__radio-input:checked'
      );

      if (!selectedRadio) {
        this.resetCartTotal();
        return;
      }

      const productPrice =
        parseFloat(
          /** @type {HTMLInputElement} */ (selectedRadio).dataset
            .productPrice || '0'
        ) || 0;
      const selectedItem = selectedRadio.closest(
        '.cart-drawer-recommendations__item'
      );
      const { input } = this.getQuantityElements(selectedItem);
      const quantity = parseInt(input?.value, 10) || 1;

      const additionalPrice = productPrice * quantity;

      const cartDrawer = document.querySelector('.cart-drawer');
      if (!cartDrawer) return;

      const originalTotal = this.getOriginalCartTotal();

      const newTotal = originalTotal + additionalPrice;

      this.updateTotalDisplays(newTotal, originalTotal);
    }

    resetCartTotal() {
      const originalTotal = this.getOriginalCartTotal();
      this.updateTotalDisplays(originalTotal, originalTotal);
    }

    getOriginalCartTotal() {
      const vatContainer = document.querySelector('.cart__vat-container');
      const dataTotal = vatContainer?.getAttribute('data-cart-total');

      if (dataTotal) {
        return parseFloat(dataTotal);
      }

      return 0;
    }

    updateTotalDisplays(newTotal, originalTotal) {
      const cartDrawer = document.querySelector('.cart-drawer');
      if (!cartDrawer) return;

      const vatRate = 1.2;
      const priceWithoutVat = newTotal;
      const priceWithVat = newTotal * vatRate;

      const withoutVatElement = cartDrawer.querySelector(
        '.cart__without-vat-value'
      );
      if (withoutVatElement) {
        withoutVatElement.textContent = this.formatMoney(priceWithoutVat);
      }

      const withVatElement = cartDrawer.querySelector('.cart__with-vat-value');
      if (withVatElement) {
        withVatElement.textContent = this.formatMoney(priceWithVat);
      }
    }

    formatMoney(cents) {
      const amount = cents / 100;
      return new Intl.NumberFormat('sk-SK', {
        style: 'currency',
        currency: 'EUR',
      }).format(amount);
    }

    async addToCart(variantId, quantity) {
      try {
        const formData = {
          items: [
            {
              id: variantId,
              quantity: quantity,
            },
          ],
          sections: this.getSectionId(),
          sections_url: window.location.pathname,
        };

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.description || 'Failed to add item to cart'
          );
        }

        const result = await response.json();

        if (result.sections) {
          this.updateCartSections(result.sections);
        }

        // Get the total cart item count from the sections HTML
        let totalItemCount = 0;
        if (result.sections) {
          const cartDrawerSection = result.sections['cart-drawer'];
          if (cartDrawerSection) {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = cartDrawerSection;
            const countElement = tempDiv.querySelector('[ref="cartItemCount"]');
            if (countElement) {
              totalItemCount = parseInt(countElement.textContent, 10) || 0;
            }
          }
          
          if (totalItemCount === 0) {
            const mainCartSection = result.sections['main-cart'];
            if (mainCartSection) {
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = mainCartSection;
              const countElement = tempDiv.querySelector('[ref="cartItemCount"]');
              if (countElement) {
                totalItemCount = parseInt(countElement.textContent, 10) || 0;
              }
            }
          }
        }

        document.dispatchEvent(
          new CartUpdateEvent(result, 'cart-recommendations', {
            itemCount: totalItemCount,
            source: 'cart-recommendations',
            sections: result.sections,
          })
        );

        return result;
      } catch (error) {
        console.error('[Recommendation] Error adding to cart:', error);
        throw error;
      }
    }

    async removeFromCart(variantId) {
      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        const item = cart.items.find((item) => item.variant_id == variantId);

        if (!item) {
          return;
        }

        const formData = {
          line: item.index + 1,
          quantity: 0,
          sections: this.getSectionId(),
          sections_url: window.location.pathname,
        };

        const response = await fetch(`${Theme.routes.cart_change_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });

        if (!response.ok) {
          throw new Error('Failed to remove item from cart');
        }

        const result = await response.json();

        if (result.sections) {
          this.updateCartSections(result.sections);
        }

        document.dispatchEvent(
          new CartUpdateEvent(result, 'cart-recommendations', {
            itemCount: result.item_count,
            source: 'cart-recommendations',
            sections: result.sections,
          })
        );

        return result;
      } catch (error) {
        console.error('[Recommendation] Error removing from cart:', error);
        throw error;
      }
    }

    async updateCartQuantity(variantId, quantity) {
      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        const item = cart.items.find((item) => item.variant_id == variantId);

        if (!item) {
          return;
        }

        const formData = {
          line: item.index + 1,
          quantity: quantity,
          sections: this.getSectionId(),
          sections_url: window.location.pathname,
        };

        const response = await fetch(`${Theme.routes.cart_change_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });

        if (!response.ok) {
          throw new Error('Failed to update cart quantity');
        }

        const result = await response.json();

        if (result.sections) {
          this.updateCartSections(result.sections);
        }

        document.dispatchEvent(
          new CartUpdateEvent(result, 'cart-recommendations', {
            itemCount: result.item_count,
            source: 'cart-recommendations',
            sections: result.sections,
          })
        );

        return result;
      } catch (error) {
        console.error('[Recommendation] Error updating cart quantity:', error);
        throw error;
      }
    }

    getSectionId() {
      const cartDrawer = document.querySelector('.cart-drawer');
      const sectionId =
        cartDrawer?.closest('[data-section-id]')?.dataset.sectionId;
      const defaultSection = sectionId || 'cart-drawer';
      
      return `${defaultSection},main-cart`;
    }

    async updateCartSections(sections) {
      Object.keys(sections).forEach((sectionId) => {
        // Check if the section exists in the DOM before trying to morph it
        const sectionElement = document.querySelector(`[data-section-id="${sectionId}"]`);
        if (sectionElement) {
          morphSection(sectionId, sections[sectionId]);
        }
      });
    }
  }

  customElements.define('cart-recommendations', CartRecommendations);
} else {
  console.log('[Recommendation] Custom element already registered');
}
